import { create } from 'zustand'
import { mailTemplatesApi } from '@/lib/api'
import { MailStructureTemplate, TemplateListItem, TemplateCreateRequest, TemplateUpdateRequest } from '@/types/template'
import { toast } from 'sonner'

interface TemplateState {
  templates: TemplateListItem[]
  currentTemplate: MailStructureTemplate | null
  isLoading: boolean

  fetchTemplates: () => Promise<void>
  fetchTemplateById: (id: number) => Promise<MailStructureTemplate>
  createTemplate: (data: TemplateCreateRequest) => Promise<MailStructureTemplate>
  updateTemplate: (id: number, data: TemplateUpdateRequest) => Promise<MailStructureTemplate>
  deleteTemplate: (id: number) => Promise<void>
  duplicateTemplate: (id: number) => Promise<MailStructureTemplate>
  setDefaultTemplate: (id: number) => Promise<void>
  setCurrentTemplate: (template: MailStructureTemplate | null) => void

  // Reset — wipes per-user data on account switch (see main.tsx auth subscribe).
  reset: () => void
}

const initialState = {
  templates: [] as TemplateListItem[],
  currentTemplate: null as MailStructureTemplate | null,
  isLoading: false,
}

export const useTemplateStore = create<TemplateState>()((set, get) => ({
  ...initialState,

  fetchTemplates: async () => {
    set({ isLoading: true })
    try {
      const response = await mailTemplatesApi.getAll()
      set({ templates: response.data, isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      toast.error('Failed to fetch templates')
    }
  },

  fetchTemplateById: async (id: number) => {
    const response = await mailTemplatesApi.getById(id)
    set({ currentTemplate: response.data })
    return response.data
  },

  createTemplate: async (data: TemplateCreateRequest) => {
    const response = await mailTemplatesApi.create(data)
    await get().fetchTemplates()
    toast.success('Template created')
    return response.data
  },

  updateTemplate: async (id: number, data: TemplateUpdateRequest) => {
    const response = await mailTemplatesApi.update(id, data)
    await get().fetchTemplates()
    toast.success('Template updated')
    return response.data
  },

  deleteTemplate: async (id: number) => {
    await mailTemplatesApi.delete(id)
    await get().fetchTemplates()
    toast.success('Template deleted')
  },

  duplicateTemplate: async (id: number) => {
    const response = await mailTemplatesApi.duplicate(id)
    await get().fetchTemplates()
    toast.success('Template duplicated')
    return response.data
  },

  setDefaultTemplate: async (id: number) => {
    await mailTemplatesApi.setDefault(id)
    await get().fetchTemplates()
    toast.success('Default template updated')
  },

  setCurrentTemplate: (template) => set({ currentTemplate: template }),

  reset: () => set(initialState),
}))
